import type { CSSProperties } from 'react'
import type { PackageCatalogItem, PackageTone } from '@proffer/shared'
import { isValidPackageAccent, normalizePackageTone } from '@proffer/shared'
import {
  packageToneAccentText,
  packageToneBadge,
  packageToneCard,
  packageToneDetail,
  packageToneDot,
  packageToneLink,
  packageToneSelectionRingClass,
} from './packageToneStyles'

export { normalizePackageTone } from '@proffer/shared'

export type PackagePresentation = {
  tone: PackageTone
  custom: boolean
  style?: CSSProperties
  badge: string
  card: string
  dot: string
  link: string
  accentText: string
  detail: (typeof packageToneDetail)[PackageTone]
  selectionRing: string
  bulletClass: string
}

export function getPackagePresentation(
  pkg: Pick<PackageCatalogItem, 'tone' | 'theme'>,
): PackagePresentation {
  const tone = normalizePackageTone(pkg.tone)
  const accent = pkg.theme?.accent?.trim()
  const accentDark = pkg.theme?.accentDark?.trim() || accent

  if (!isValidPackageAccent(accent)) {
    return {
      tone,
      custom: false,
      badge: packageToneBadge[tone],
      card: packageToneCard[tone],
      dot: packageToneDot[tone],
      link: packageToneLink[tone],
      accentText: packageToneAccentText[tone],
      detail: packageToneDetail[tone],
      selectionRing: packageToneSelectionRingClass(tone),
      bulletClass: packageToneAccentText[tone],
    }
  }

  const style = {
    '--pkg-accent': accent,
    '--pkg-accent-dark': isValidPackageAccent(accentDark) ? accentDark : accent,
  } as CSSProperties

  return {
    tone,
    custom: true,
    style,
    badge: 'pkg-theme-badge',
    card: 'pkg-theme-card',
    dot: 'pkg-theme-dot',
    link: 'pkg-theme-link',
    accentText: 'pkg-theme-accent-text',
    detail: {
      badge: 'pkg-theme-badge',
      panel: 'pkg-theme-panel',
      navCard: 'pkg-theme-nav-card',
      activeRing: 'pkg-theme-active-ring',
      dot: 'pkg-theme-dot',
      button: 'pkg-theme-button',
    },
    selectionRing: 'pkg-theme-ring',
    bulletClass: 'pkg-theme-accent-text',
  }
}

/** Wrapper props για κάρτα / panel με δυναμικό χρώμα. */
export function packageSurfaceProps(pkg: Pick<PackageCatalogItem, 'tone' | 'theme'>) {
  const pres = getPackagePresentation(pkg)
  return {
    className: pres.custom ? `pkg-theme-root ${pres.card}` : pres.card,
    style: pres.style,
    presentation: pres,
  }
}
